<script setup>
import {onMounted, ref, computed, nextTick, watch} from "vue";
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import axios from "axios";
import { useAuthStore } from '@/stores/auth.store.js';

const authStore = useAuthStore();
const { user } = storeToRefs(authStore);
const router = useRouter();


const roles = ref([]);
const permissionsList = ref([]);

const currentPage = ref(1);
const perPage = ref(10);
const totalPages = ref(1);
const totalRoles = ref(0);

const loading = ref(false);
const search = ref('');

const showModal = ref(false);
const isEditMode = ref(false);

const currentRole = ref({
  id: null,
  name: '',
  selectedPermissions: []
});

const fetchRoles = async (page = 1) => {
  loading.value = true;
  try {
    const res = await axios.get(`${import.meta.env.VITE_API_BACKEND_URL}/api/admin/roles`, {
      params: {page, per_page: perPage.value, search: search.value || undefined},
      withCredentials: true
    });

    roles.value = res.data.data || res.data.roles || [];
    currentPage.value = res.data.current_page || page;
    totalPages.value = res.data.total_pages || res.data.last_page || 1;
    totalRoles.value = res.data.total || 0;
  } catch (err) {
    console.error(err);
    alert('Failed to load roles');
  } finally {
    loading.value = false;
  }
};

const fetchAllPermissions = async () => {
  try {
    const res = await axios.get(`${import.meta.env.VITE_API_BACKEND_URL}/api/admin/permissions`, {
      withCredentials: true
    });


    permissionsList.value = res.data.data ?? res.data ?? [];


  } catch (err) {
    console.error('Failed to load permissions', err);
  }
};

const togglePermission = (permId) => {
  const index = currentRole.value.selectedPermissions.indexOf(permId);
  if (index > -1) {
    currentRole.value.selectedPermissions.splice(index, 1);
  } else {
    currentRole.value.selectedPermissions.push(permId);
  }
};

const editRole = async (role) => {
  isEditMode.value = true;

  const perms = role.permissions || [];
  const selectedIds = Array.isArray(perms)
      ? perms.map((p) => typeof p === 'object' ? p.id : Number(p)).filter(id => !isNaN(id))
      : [];

  currentRole.value.id = role.id;
  currentRole.value.name = role.name || '';
  currentRole.value.selectedPermissions = [...selectedIds];

  showModal.value = true;


  await nextTick();
};

const openAddModal = () => {
  isEditMode.value = false;

  currentRole.value.id = null;
  currentRole.value.name = '';
  currentRole.value.selectedPermissions = [];

  showModal.value = true;
};

const saveRole = async () => {
  try {
    const payload = {
      name: currentRole.value.name.trim(),
      permission_ids: currentRole.value.selectedPermissions
    };

    if (isEditMode.value && currentRole.value.id) {
      await axios.put(`${import.meta.env.VITE_API_BACKEND_URL}/api/admin/roles/${currentRole.value.id}`, payload, {withCredentials: true});
      alert('Role updated successfully');
    } else {
      await axios.post(`${import.meta.env.VITE_API_BACKEND_URL}/api/admin/roles`, payload, {withCredentials: true});
      alert('Role created successfully');
    }

    showModal.value = false;
    fetchRoles(currentPage.value);
  } catch (err) {
    alert(err.response?.data?.error || 'Error saving role');
  }
};

const deleteRole = async (id) => {
  if (!confirm('Are you sure you want to delete this role?')) return;

  try {
    await axios.delete(`${import.meta.env.VITE_API_BACKEND_URL}/api/admin/roles/${id}`, {withCredentials: true});
    alert('Role deleted successfully');
    fetchRoles(currentPage.value);
  } catch (err) {
    alert(err.response?.data?.error || 'Failed to delete role');
  }
};

const changePage = (page) => {
  if (page < 1 || page > totalPages.value) return;
  fetchRoles(page);
};

const pages = computed(() => Array.from({length: totalPages.value}, (_, i) => i + 1));

const isAdmin = computed(() => {
  return user.value?.role?.name === 'admin'
});

watch(() => isAdmin.value, (isAdminValue) => {
  if (isAdminValue === false && user.value !== null) {
    alert("You do not have access to role management.");
    router.push('/');
  }
}, { immediate: true });


const isLoadingUser = computed(() => !user.value);




const handleSearch = () => {
  currentPage.value = 1;
  fetchRoles(1);
};



onMounted(() => {
  if (!isAdmin.value) return;
  fetchRoles();
  fetchAllPermissions();
});
</script>

<template>
  <div v-if="isLoadingUser" class="text-center py-5">
    <div class="spinner-border text-primary" role="status"></div>
    <p class="mt-3">Checking access rights...</p>
  </div>

  <div v-else-if="!isAdmin" class="text-center py-5">
    <div class="alert alert-danger">
      <h4>Access is denied</h4>
      <p>You do not have permission to view this page.</p>
      <button class="btn btn-primary mt-3" @click="router.push('/')">
        Return to home page
      </button>
    </div>
  </div>



  <div v-else class="container mt-4">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h1>Role Management</h1>
      <button class="btn btn-success" @click="openAddModal">
        <i class="bi bi-plus-lg"></i> Add New Role
      </button>
    </div>

    <div class="row mb-3">
      <div class="col-md-6">
        <input
            v-model="search"
            @keyup.enter="handleSearch"
            type="text"
            class="form-control"
            placeholder="Search by role name..."
        >
      </div>
      <div class="col-md-2">
        <button class="btn btn-primary w-100" @click="handleSearch">Search</button>
      </div>
    </div>

    <div class="card">
      <div class="card-body">
        <div v-if="loading" class="text-center py-4">
          <div class="spinner-border text-primary" role="status"></div>
          <p class="mt-2">Loading roles...</p>
        </div>

        <table v-else class="table table-hover align-middle">
          <thead class="table-light">
          <tr>
            <th>ID</th>
            <th>Role Name</th>
            <th>Permissions</th>
            <th class="text-end">Actions</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="role in roles" :key="role.id">
            <td>{{ role.id }}</td>
            <td><strong>{{ role.name }}</strong></td>
            <td>
                <span
                    v-for="perm in role.permissions"
                    :key="perm.id"
                    class="badge bg-primary me-1 mb-1">
                  {{ perm.name }}
                </span>
              <span v-if="!role.permissions || role.permissions.length === 0" class="text-muted">No permissions</span>
            </td>
            <td class="text-end">
              <button class="btn btn-sm btn-warning me-2" @click="editRole(role)" title="Edit">
                <i class="bi bi-pencil"></i>
              </button>
              <button v-if="role.name!='admin' && role.name!='user'" class="btn btn-sm btn-danger"
                      @click="deleteRole(role.id)" title="Delete">
                <i class="bi bi-trash"></i>
              </button>
            </td>
          </tr>
          <tr v-if="roles.length === 0">
            <td colspan="4" class="text-center py-4 text-muted">No roles found</td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>

    <nav class="mt-4" v-if="totalPages > 1">
      <ul class="pagination justify-content-center">
        <li class="page-item" :class="{ disabled: currentPage === 1 }">
          <button class="page-link" @click="changePage(currentPage - 1)">Previous</button>
        </li>
        <li v-for="p in pages" :key="p" class="page-item" :class="{ active: p === currentPage }">
          <button class="page-link" @click="changePage(p)">{{ p }}</button>
        </li>
        <li class="page-item" :class="{ disabled: currentPage === totalPages }">
          <button class="page-link" @click="changePage(currentPage + 1)">Next</button>
        </li>
      </ul>
    </nav>

    <div class="modal fade" :class="{ show: showModal }" tabindex="-1" style="display: block;" v-if="showModal">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ isEditMode ? 'Edit Role' : 'Create New Role' }}</h5>
            <button type="button" class="btn-close" @click="showModal = false"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">Role Name <span class="text-danger">*</span></label>
              <input
                  :readonly="isEditMode && (currentRole.name === 'admin' || currentRole.name === 'user')"
                  v-model="currentRole.name"
                  class="form-control" required>
            </div>

            <div class="mb-4">
              <label class="form-label">Permissions <span class="text-danger">*</span></label>
              <div class="row g-2">
                <div v-for="perm in permissionsList" :key="perm.id" class="col-md-6">
                  <div class="form-check">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        :id="'perm-' + perm.id"
                        :checked="currentRole.selectedPermissions.includes(perm.id)"
                        @change="togglePermission(perm.id)"
                    >
                    <label class="form-check-label" :for="'perm-' + perm.id">
                      <strong>{{ perm.name }}</strong>
                      <small v-if="perm.description" class="text-muted d-block">{{ perm.description }}</small>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showModal = false">Cancel</button>
            <button type="button" class="btn btn-primary" @click="saveRole">
              {{ isEditMode ? 'Save Changes' : 'Create Role' }}
            </button>
          </div>
        </div>
      </div>
    </div>


    <div class="modal-backdrop fade show" v-if="showModal" @click="showModal = false"></div>
  </div>
</template>